import DataTable from '@/components/table/data-table';
import { useTenantCohortsForVariables } from '@/hooks/queries/cohorts';
import { useCohortVariables } from '@/hooks/queries/variables/cohort-variables';
import {
  buildCohortValueMap,
  formatCohortVariableValue,
  Variable,
} from '@/types/variables';
import { stripCohortVariablePrefix } from '@/utils/promptUtils';
import { Icon } from '@iconify/react';
import { Button, Spinner } from '@nextui-org/react';
import { useMemo } from 'react';

function formatVariableCreatedBy(createdBy: Variable['created_by']): string {
  if (!createdBy?.first_name && !createdBy?.last_name) {
    return '—';
  }

  return `${createdBy.first_name ?? ''} ${createdBy.last_name ?? ''}`.trim();
}

function formatVariableCreatedAt(createdAt: Variable['created_at']): string {
  if (!createdAt) {
    return '—';
  }

  return new Date(createdAt).toLocaleString();
}

interface CohortVariablesTabProps {
  onEditVariable: (variable: Variable) => void;
}

export function CohortVariablesTab({
  onEditVariable,
}: CohortVariablesTabProps) {
  const { data, isLoading: isLoadingVariables } = useCohortVariables();
  const { data: cohortsData, isLoading: isLoadingCohorts } =
    useTenantCohortsForVariables();

  const cohorts = cohortsData?.results ?? [];
  const variables = data?.results ?? [];

  const tableConfig = useMemo(() => {
    return {
      columns: [
        {
          uid: 'id',
          name: 'ID',
          label: 'ID',
          sortable: true,
        },
        {
          uid: 'name',
          name: 'Variable Name',
          label: 'Variable Name',
          sortable: true,
          renderCell: (variable: Variable) => (
            <span>
              <span className="text-default-400">cohort.</span>
              {stripCohortVariablePrefix(variable.name)}
            </span>
          ),
        },
        {
          uid: 'value',
          name: 'Value',
          label: 'Value',
          sortable: false,
          wrap: true,
          renderCell: (variable: Variable) => {
            if (isLoadingCohorts || cohorts.length === 0) {
              return <span className="text-foreground-400">—</span>;
            }

            const valueMap = buildCohortValueMap(variable.values);

            return (
              <div className="max-h-[5.5rem] overflow-y-auto py-0.5">
                <div className="flex flex-col gap-3">
                  {cohorts.map((cohort) => {
                    const value = valueMap.get(cohort.id) ?? null;
                    const formattedValue = formatCohortVariableValue(value);

                    return (
                      <div key={cohort.id}>
                        <div className="text-sm font-bold">{cohort.name}</div>
                        <div className="whitespace-pre-wrap break-words text-sm leading-[1.375rem] text-foreground-600">
                          {formattedValue}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          },
        },
        {
          uid: 'created_by',
          name: 'Created By',
          label: 'Created By',
          renderCell: (variable: Variable) =>
            formatVariableCreatedBy(variable.created_by),
        },
        {
          uid: 'created_at',
          name: 'Created At',
          label: 'Created At',
          sortable: true,
          renderCell: (variable: Variable) =>
            formatVariableCreatedAt(variable.created_at),
        },
        {
          uid: 'actions',
          name: 'Actions',
          label: 'Actions',
          renderCell: (variable: Variable) => (
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEditVariable(variable)}
            >
              <Icon icon="akar-icons:edit" width="20" />
            </Button>
          ),
        },
      ],
      initialVisibleColumns: [
        'id',
        'name',
        'value',
        'created_by',
        'created_at',
        'actions',
      ],
      searchable: true,
      searchPlaceholder: 'Search variables...',
      searchKey: 'name',
      initialSortDescriptor: {
        column: 'name',
        direction: 'ascending' as const,
      },
      withoutBorder: false,
      layout: 'fixed' as const,
      disableMaxHeight: true,
      bottomContentPlacement: 'outside' as const,
      stickyHeader: false,
    };
  }, [cohorts, isLoadingCohorts, onEditVariable]);

  if (isLoadingVariables || isLoadingCohorts) {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return <DataTable config={tableConfig} data={variables} />;
}
